/**
 * FeatureFlagsPanel Component
 *
 * Panel for managing feature flags with toggles.
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button, Input, SettingRow, Skeleton, Switch } from '@elbklar/ui';
import { useFeatureFlags, useSetFeatureFlag } from '@/hooks/useFeatureFlags';

// Known feature flags with descriptions
const KNOWN_FLAGS: Record<string, { label: string; description: string }> = {
  gmail_sync: {
    label: 'Gmail-Synchronisation',
    description: 'Automatische E-Mail-Synchronisation mit Gmail',
  },
  ai_features: {
    label: 'KI-Funktionen',
    description: 'KI-gestützte Funktionen wie automatische Zusammenfassungen',
  },
  public_api: {
    label: 'Öffentliche API',
    description: 'Externen Zugriff auf die API ermöglichen',
  },
  audit_log: {
    label: 'Audit-Log',
    description: 'Änderungsverlauf für alle Datensätze',
  },
  two_factor_auth: {
    label: 'Zwei-Faktor-Authentifizierung',
    description: '2FA für zusätzliche Sicherheit',
  },
};

export function FeatureFlagsPanel() {
  const { data: flags, isLoading, isError } = useFeatureFlags();
  const setFlagMutation = useSetFeatureFlag();
  const [newFlagName, setNewFlagName] = useState('');

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center justify-between rounded-lg border border-border p-4">
            <div className="space-y-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-48" />
            </div>
            <Skeleton className="h-6 w-12 rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-8 text-center text-danger">Funktionen ließen sich nicht laden</div>
    );
  }

  const allFlags = { ...flags };

  // Add known flags that don't exist yet
  for (const key of Object.keys(KNOWN_FLAGS)) {
    if (!(key in allFlags)) {
      allFlags[key] = false;
    }
  }

  const handleToggle = async (flag: string, currentValue: boolean) => {
    await setFlagMutation.mutateAsync({
      flag,
      enabled: !currentValue,
    });
  };

  const handleAddFlag = async () => {
    if (!newFlagName.trim()) return;

    const normalizedName = newFlagName.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    await setFlagMutation.mutateAsync({
      flag: normalizedName,
      enabled: false,
    });
    setNewFlagName('');
  };

  return (
    <div className="space-y-6">
      {/* Feature Flag List — Schalter gelten sofort (README Switch) */}
      <div className="space-y-3">
        {Object.entries(allFlags).map(([flag, enabled]) => {
          const knownFlag = KNOWN_FLAGS[flag];
          return (
            <SettingRow
              key={flag}
              title={knownFlag?.label || flag}
              description={knownFlag?.description ?? <span className="t-mono">{flag}</span>}
              status={enabled ? { tone: 'success', label: 'An' } : { tone: 'neutral', label: 'Aus' }}
              control={
                <Switch
                  label={knownFlag?.label || flag}
                  checked={enabled}
                  disabled={setFlagMutation.isPending}
                  onChange={() => handleToggle(flag, enabled)}
                />
              }
            />
          );
        })}
      </div>

      {/* Add New Flag */}
      <div className="border-t border-border pt-4">
        <h4 className="mb-3 t-title">Funktion hinzufügen</h4>
        <div className="flex gap-3">
          <Input
            type="text"
            aria-label="Name der neuen Funktion"
            value={newFlagName}
            onChange={(e) => setNewFlagName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddFlag()}
            className="flex-1"
            placeholder="feature_name (snake_case)"
          />
          <Button icon={Plus} onClick={handleAddFlag} disabled={!newFlagName.trim() || setFlagMutation.isPending}>
            Funktion hinzufügen
          </Button>
        </div>
        <p className="mt-2 t-small text-muted-foreground">Namen in snake_case, etwa neue_funktion</p>
      </div>

      {/* Status Messages */}
      {setFlagMutation.isSuccess && <div className="t-small text-success">Funktion gespeichert</div>}
      {setFlagMutation.isError && <div className="t-small text-danger">Funktion ließ sich nicht speichern</div>}
    </div>
  );
}

export default FeatureFlagsPanel;
